import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UpdateRoleComponent } from './update-role/update-role.component';
import { CreateRoleComponent } from './create-role/create-role.component';
import { ApplicationDto, PermissionDto, RoleDto } from '@clearing-hub/common';
import { RoleState } from '../../store/Role/RoleReducer';
import { ChangeApplication, DeleteRole, InitPermissions } from '../../store/Role/RoleActions';
import { initApplicationSelector, initRoleSelector } from '../../store/Role/RoleSelector';
import { RoleService } from '../../service/role.service';
import { NgxBootstrapConfirmConfig } from '../ngx-bootstrap-confirm/NgxBootstrapConfirmConfig';
import { TableColumn } from '@siemens/ngx-datatable/lib/types/table-column.type';
import { NgTemplateOutlet } from '@angular/common';
import { NgxBootstrapConfirmService } from '../ngx-bootstrap-confirm/ngx-bootstrap-confirm.service';
import { ColumnMode } from '@siemens/ngx-datatable';
import { ChangeApplicationPayload } from '../../model/ChangeApplicationPayload';

@Component({
  selector: 'fxch-role',
  templateUrl: './role.component.html',
  styleUrls: ['./role.component.scss']
})
export class RoleComponent implements OnInit, OnDestroy {

  @ViewChild('nameTmpl', { static: true }) nameTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('descriptionTmpl', { static: true }) descriptionTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateTmpl', { static: true }) updateTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('deleteTmpl', { static: true }) deleteTmpl: TemplateRef<NgTemplateOutlet>;

  @ViewChild('nameCellTmpl', { static: true }) nameCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('descriptionCellTmpl', { static: true }) descriptionCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateCellTmpl', { static: true }) updateCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('deleteCellTmpl', { static: true }) deleteCellTmpl: TemplateRef<NgTemplateOutlet>;

  applications: Array<ApplicationDto>;
  roles: Array<RoleDto>;
  permissions: Array<PermissionDto>;
  selectedApplication: ApplicationDto;

  columns: Array<TableColumn>;

  ColumnMode = ColumnMode;


  private subscriptions: Array<Subscription> = new Array<Subscription>();

  constructor(private readonly modalService: BsModalService,
              private readonly roleService: RoleService,
              private readonly ngxBootstrapConfirmService: NgxBootstrapConfirmService,
              private readonly toastrService: ToastrService,
              private readonly roleStore: Store<RoleState>,
              private readonly activatedRoute: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.roleStore.dispatch(new InitPermissions(this.activatedRoute.snapshot.data['preload']));
    this.subscriptions.push(
      this.roleStore.select(initRoleSelector).subscribe((roles) => {
        this.roles = Object.values(roles);
      })
    );
    this.subscriptions.push(
      this.roleStore.select(initApplicationSelector).subscribe((applications) => {
        this.applications = applications;
      })
    );

    this.selectFirstApplication();

    this.columns = [
      {
        headerTemplate: this.nameTmpl,
        cellTemplate: this.nameCellTmpl,
        name: 'Name',
        width: 275,
        prop: 'name',
        maxWidth: 350
      },
      {
        headerTemplate: this.descriptionTmpl,
        cellTemplate: this.descriptionCellTmpl,
        name: 'Description',
        prop: 'description'
      }, {
        headerTemplate: this.updateTmpl,
        cellTemplate: this.updateCellTmpl,
        name: 'Update role',
        minWidth: 100,
        maxWidth: 150,
        width: 125
      }, {
        headerTemplate: this.deleteTmpl,
        cellTemplate: this.deleteCellTmpl,
        name: 'Remove role',
        minWidth: 100,
        maxWidth: 150,
        width: 125
      }
    ];
  }

  selectFirstApplication() {
    if (this.applications && this.applications[0]) {
      this.selectedApplication = this.applications[0];
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  openUpdateModal(r: RoleDto) {
    const initialState = {
      role: r
    }
    this.modalService.show(UpdateRoleComponent, {
      initialState
    });
  }

  openCreateModal() {
    const initialState = {
      application: this.selectedApplication
    }
    this.modalService.show(CreateRoleComponent, {
      initialState
    });
  }


  deleteRole(r: RoleDto) {
    const options: NgxBootstrapConfirmConfig = {
      title: 'Are you sure want to delete this role with all its permissions?',
      confirmLabel: 'Ok',
      declineLabel: 'Cancel'
    }

    this.ngxBootstrapConfirmService.confirm(options).then((res: boolean) => {
      if (res) {
        this.roleService.deleteRole(r.id).subscribe(() => {
          this.roleStore.dispatch(new DeleteRole(r.id));
          this.toastrService.success('Successfully deleted');
        }, err => {
          this.toastrService.error(err.error.message);
        });
      }
    });
  }

  changeApplication(application: ApplicationDto) {
    this.roleService.findRoles({applicationId: application.id}).subscribe((resp) => {
      this.roleStore.dispatch(new ChangeApplication(new ChangeApplicationPayload(resp.roles, resp.permissions)));
    });
  }
}
